// --------------------------------------------------
//   Imports
// --------------------------------------------------


// React
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Material UI
import {
    Box,
    Button,
    Grid2 as Grid,
    Stack,
    Typography,
} from "@mui/material";

// Custom Contexts
import { UserContext } from "../Contexts";

// Custom Components
import Header from "../components/header/Header";
import Footer from "../components/footer/Footer";
import LoadingIcon from "../components/loading/LoadingIcon";
import Error from "../components/Error";

// API
import { fetchUser } from '../api/user-service';

// Custom Scripts
import { LoadedDataState, useLoadedData } from "../scripts/loadedData/loadedData";
import { ErrorState, NotLoggedInError } from "../scripts/errorHandling";



// --------------------------------------------------
//   Main Component
// --------------------------------------------------


function BaseLayout({ scrollable = true, children }) {

  // Context
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [ userData, manageUserData ] = useLoadedData();

  // Check whether we're on the login page
  const isLoginPage = location.pathname === '/login'

  // Retrieve the current user, tracking the status & result in the userData object
  // Effect is called every time the user data state changes,
  // but only proceeds with the lookup if the state has been set to NONE.
  useEffect(() => {

    // If already working, skip
    if (userData.state !== LoadedDataState.NONE) return;

    // Fetch the data
    manageUserData.setWorking();
    fetchUser().then(
      data  => manageUserData.setSuccess( data ),
      error => {

        // Intercept login errors and redirect to the login page (if not already there)
        // Clears retrieved user data to re-trigger lookup, otherwise login page will be blank
        // TODO: Can we go directly to the login page without having to re-trigger?
        if ( error instanceof NotLoggedInError && !isLoginPage ) {
          manageUserData.clearData();
          return navigate('/login');
        }

        // Otherwise, set the failure state
        else {
          manageUserData.setFailure( ErrorState.NO_USER_DATA, error )
        }
      },
    );
  }, [ userData.state, location.pathname ]);

  const refreshUserData = () => {
    manageUserData.clearData();
  }

  // Main content
  return (
    <Box height="100%">
      <UserContext.Provider value={{ userData, refreshUserData }}>
        <Stack width="100%" height="100%" spacing={0} backgroundColor="#FAF9F6">
          <Header />

          <Box
            flexGrow={1} minHeight="0" padding="48px" backgroundColor="#E4E0EE"
            sx={{ overflowY: scrollable ? 'scroll' : 'auto' }}
          >
            {

              // If user data is still being loaded, show a loader icon
              ( userData.state === LoadedDataState.WORKING || userData.state === LoadedDataState.NONE )
                ? <LoadingIcon />
                :

              // If user data load failed, show an error state
              ( userData.state === LoadedDataState.FAILURE && !isLoginPage )
                ? <Error message={ userData.error.message } />
                :

              // On success, show the main content
              children
            }
          </Box>

          <Footer />
        </Stack>
      </UserContext.Provider>
    </Box>
  )
}



// --------------------------------------------------
//   Export(s)
// --------------------------------------------------

export default BaseLayout;
